/**
 * Feature flags — the system-wide on/off panel.
 *
 * The gift-card rail is pulled to the top and highlighted: it ships off and an operator
 * turns it on here, which is exactly the kind of one-write, no-deploy control this page
 * exists for. Toggling is gated on `flags:write` (superadmin); everyone else sees the
 * live state read-only. Writes are optimistic and reconciled against the API response.
 *
 * The page also carries the two verification-code delivery switches, which are not rows in
 * `feature_flags` and deliberately were not made into rows in `feature_flags`. See
 * `DeliverySwitches` below for the full argument.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { Flag, PlatformSettings, SetPlatformSettingsRequest } from '../contracts/types.js';
import { Badge, Card, ErrorBox, LoadingState, Switch } from '../components/ui.js';
import { IconAlert, IconChat, IconGift, IconMail } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { humanize, relativeTime } from '../lib/format.js';

const FEATURED_KEY = 'gift_cards';

/**
 * The one flag on this page that takes something away from customers who already have it.
 *
 * Every other switch here answers "does this rail exist yet". This one answers "may an
 * unverified customer use the rails at all", and turning it on stops accounts that were
 * transacting an hour ago — including accounts holding a balance. That is a legitimate thing
 * for a licensed operator to need and it is not something anybody should discover by clicking
 * a toggle in a list of ten identical-looking toggles, so it gets its own explanation, its own
 * confirmation, and a plain statement of who it affects.
 *
 * The copy names the consequence for people rather than the mechanism: the operator does not
 * need to know it is a preHandler, they need to know a customer with money in the account will
 * be stopped mid-transfer and shown a prompt to verify.
 */
const KYC_KEY = 'require_kyc';

/**
 * Said before the switch moves, because afterwards is too late for the customer it stops.
 *
 * Deliberately concrete about the blast radius — existing accounts, not just new signups —
 * since that is the part an operator is most likely to have assumed the other way round.
 */
const KYC_CONFIRM = [
  'Turn on mandatory identity verification?',
  '',
  'Every customer below Tier 2 will immediately be unable to send, withdraw, convert or pay bills, including existing customers who hold a balance and were transacting today. They keep full access to their balances, their history and support, and the app prompts them to verify with a BVN or NIN.',
  '',
  'Deposits are unaffected: incoming money still lands. Automatic settlements are held in the customer’s wallet rather than failed.',
  '',
  'Turning this back off restores access immediately.',
].join('\n');

export function FlagsPage(): JSX.Element {
  const { api, session } = useAuth();
  const toast = useToast();
  const canWrite = session !== null && adminCan(session.me.role, 'flags:write');
  // The delivery switches live on the business-settings row, which the API opens to a super
  // admin alone — on the read as well as the write. So this is one grant, not two: an operator
  // who cannot change them cannot see them either, and asking for them anyway would answer 403
  // and paint an error across a page they are otherwise entitled to read.
  const canSeeDelivery = session !== null && adminCan(session.me.role, 'admins:write');
  const state = useAsync(() => api.listFlags(), []);
  const [pending, setPending] = useState<string | null>(null);

  async function toggle(flag: Flag, next: boolean) {
    // The only confirm on this page, on the only switch whose "on" position removes access
    // from customers who currently have it. Turning it back off needs no ceremony — that
    // direction only ever restores access — which is why the guard is one-sided.
    if (flag.key === KYC_KEY && next && !window.confirm(KYC_CONFIRM)) return;

    setPending(flag.key);
    state.setData((prev) =>
      prev === null ? prev : { flags: prev.flags.map((f) => (f.key === flag.key ? { ...f, enabled: next } : f)) },
    );
    try {
      const updated = await api.setFlag(flag.key, { enabled: next });
      state.setData((prev) =>
        prev === null ? prev : { flags: prev.flags.map((f) => (f.key === flag.key ? updated : f)) },
      );
      // The generic copy reads "customers can now reach this feature", which is true of every
      // flag here except one: enabling mandatory KYC takes access away rather than granting it.
      // A confirmation that describes the opposite of what just happened is worse than none.
      toast.success(
        `${humanize(flag.key)} ${next ? 'enabled' : 'disabled'}`,
        flag.key === KYC_KEY
          ? next
            ? 'Customers below Tier 2 can no longer send, withdraw, convert or pay bills. They keep their balances, their history and support, and are prompted to verify.'
            : 'Verification is optional again. Everyone who was blocked by it can transact immediately, within their tier ceilings.'
          : next
            ? 'Customers can now reach this feature.'
            : 'This feature is now hidden from customers.',
      );
    } catch (err) {
      state.setData((prev) =>
        prev === null
          ? prev
          : { flags: prev.flags.map((f) => (f.key === flag.key ? { ...f, enabled: !next } : f)) },
      );
      toast.error('Could not update flag', messageOf(err));
    } finally {
      setPending(null);
    }
  }

  if (state.loading) return <LoadingState label="Loading feature flags…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (state.data === null) return <ErrorBox message="No flag data." />;

  const featured = state.data.flags.find((f) => f.key === FEATURED_KEY);
  const kyc = state.data.flags.find((f) => f.key === KYC_KEY);
  const rest = state.data.flags.filter((f) => f.key !== FEATURED_KEY && f.key !== KYC_KEY);

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Feature flags</h1>
        <p className="page-head__sub">
          The system-wide switches that decide what customers can reach. A flag turned off returns a
          safe “unavailable” to the app. Never a deploy, never a 404.
          {!canWrite && ' Your role can view flags but not change them.'}
        </p>
      </div>

      {featured !== undefined && (
        <Card className="flags-featured" flush title={undefined}>
          <FlagRow
            flag={featured}
            featured
            canWrite={canWrite}
            busy={pending === featured.key}
            onToggle={toggle}
          />
          <div className="flag-row__note">
            {featured.enabled
              ? 'The rail is live. Customers can submit gift cards.'
              : 'While off, the rail is dark to customers.'}{' '}
            Submitted redemptions are approved or rejected on the{' '}
            <Link to="/giftcards">gift-card review queue</Link>, where approving one credits the
            customer’s naira wallet.
          </div>
        </Card>
      )}

      <div style={{ height: 16 }} />

      {kyc !== undefined && (
        <>
          <Card className="flags-featured" flush title={undefined}>
            <FlagRow
              flag={kyc}
              featured
              canWrite={canWrite}
              busy={pending === kyc.key}
              onToggle={toggle}
            />
            <div className="flag-row__note">
              {kyc.enabled
                ? 'On. Customers below Tier 2 cannot send, withdraw, convert or pay bills, and are prompted in the app to verify with a BVN or NIN. They can still see their balances and history, reach support and sign out; deposits still land, and automatic settlements are held in the wallet rather than failed.'
                : 'Off. Verification is optional and tier ceilings alone decide what a customer may move, which is how this platform behaves unless you change it here.'}{' '}
              This is the only switch on this page that takes access away from customers who
              already have it, existing balances included. Ceilings per tier are set on the{' '}
              {/* The page has been called "Spending limits" everywhere else since the
                  plain-language pass; this link still said "tier limits", which is a name the
                  sidebar no longer has. */}
              <Link to="/kyc/limits">Spending limits</Link> page.
            </div>
          </Card>
          <div style={{ height: 16 }} />
        </>
      )}

      {canSeeDelivery ? (
        <>
          <DeliverySwitches />
          <div style={{ height: 16 }} />
        </>
      ) : (
        <>
          <Card title="Verification codes">
            <div className="muted">
              A customer signing in on a new phone is sent a one-time code by email or by text
              message. Which of those two ways are switched on is something only a super admin
              can see or change.
            </div>
          </Card>
          <div style={{ height: 16 }} />
        </>
      )}

      <Card flush title="All flags" actions={<Badge tone="neutral">{state.data.flags.length} total</Badge>}>
        {rest.map((flag) => (
          <FlagRow
            key={flag.key}
            flag={flag}
            canWrite={canWrite}
            busy={pending === flag.key}
            onToggle={toggle}
          />
        ))}
      </Card>
    </>
  );
}

/**
 * The two verification-code delivery switches, on this page and **not** copied into
 * `feature_flags`.
 *
 * ## Why they are here at all
 *
 * An operator does not hold a mental list of "flags" and a separate list of "settings". They
 * hold one list: the things on this platform I can turn on and off. Email and text-message
 * delivery are squarely on that list — an operator turns one off the night their SMS vendor
 * starts swallowing messages — and they were reachable through no screen at all, sitting in a
 * column that the console read and never offered. So they belong on the page that answers
 * "what can I switch", which is this one.
 *
 * ## Why they are not rows in `feature_flags`
 *
 * The obvious move was to add `device_otp_email` and `device_otp_sms` to the flags table so
 * they would be ordinary rows like `bank_transfer` and `sell`. That would have been wrong, and
 * the reason is worth writing down because the same temptation will come round again.
 *
 * `platform_settings.device_otp_email_enabled` and `..._sms_enabled` are already the source of
 * truth: the sign-in path reads those two columns directly and nothing else. Adding flag rows
 * would not move the truth, it would add a second copy of it — and the moment there are two
 * copies, an operator can turn a switch off in one place and watch codes keep going out from
 * the other, with the console showing "off" and the platform behaving "on". A switch that lies
 * about whether it worked is worse than a switch that is hard to find. The only honest way to
 * have two homes for one control is to have one home and show it in two places, which is what
 * this does: the page is new, the storage is not, and there is still exactly one answer.
 *
 * The write goes through the same business-settings patch a super admin has always had, so the
 * change is audited exactly as every other configuration change is, under the same admin id.
 *
 * ## Why the copy says what it says
 *
 * Each switch names what stops happening when it is off, and what happens to the customer
 * instead, because that is the question an operator is actually asking at the moment they reach
 * for it. Both off is a real and legitimate position, so it is spelled out rather than blocked:
 * a new phone is then let in on the password and the PIN alone.
 */
function DeliverySwitches(): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const state = useAsync(() => api.getBusinessSettings(), []);
  const [pending, setPending] = useState<DeliveryKey | null>(null);

  async function toggle(key: DeliveryKey, next: boolean) {
    setPending(key);
    // Optimistic, then reconciled against what the server actually stored, exactly as the flag
    // rows above are. A failed write puts the switch back where it was and says so.
    state.setData((prev) => (prev === null ? prev : withDelivery(prev, key, next)));
    try {
      const patch: SetPlatformSettingsRequest =
        key === 'deviceOtpEmailEnabled'
          ? { deviceOtpEmailEnabled: next }
          : { deviceOtpSmsEnabled: next };
      const updated = await api.setBusinessSettings(patch);
      state.setData(() => updated);
      toast.success(
        `Verification codes by ${DELIVERY_COPY[key].channel} are now ${next ? 'on' : 'off'}`,
        DELIVERY_COPY[key].toast[next ? 'on' : 'off'],
      );
    } catch (err) {
      state.setData((prev) => (prev === null ? prev : withDelivery(prev, key, !next)));
      toast.error('Could not change that switch', messageOf(err));
    } finally {
      setPending(null);
    }
  }

  return (
    <Card
      flush
      title="Verification codes"
      actions={<Badge tone="neutral">Sign-in on a new phone</Badge>}
    >
      {state.loading ? (
        <div style={{ padding: 20 }}>
          <LoadingState label="Loading the code settings…" />
        </div>
      ) : state.error !== null ? (
        <div style={{ padding: 20 }}>
          <ErrorBox message={state.error} />
        </div>
      ) : state.data === null ? (
        <div style={{ padding: 20 }}>
          <ErrorBox message="The code settings are not available." />
        </div>
      ) : (
        <>
          <DeliveryRow
            settings={state.data}
            deliveryKey="deviceOtpEmailEnabled"
            busy={pending === 'deviceOtpEmailEnabled'}
            onToggle={toggle}
          />
          <DeliveryRow
            settings={state.data}
            deliveryKey="deviceOtpSmsEnabled"
            busy={pending === 'deviceOtpSmsEnabled'}
            onToggle={toggle}
          />
          <div className="flag-row__note">
            {!state.data.deviceOtpEmailEnabled && !state.data.deviceOtpSmsEnabled
              ? 'Both are off, so no codes are being sent at all. Signing in on a new phone now needs the password and the PIN and nothing else. That is a real choice to make while a vendor is down, and it is worth turning one back on afterwards.'
              : 'A code is only ever sent to an email address or phone number the customer has already confirmed. Email is tried first, then text message, so the second switch is what a customer falls back to when the first way is off or was never confirmed.'}{' '}
            This is the only place these two are set. A change is saved with the rest of your
            platform settings, is recorded against your name, and applies to the next sign-in.
            Text messages also depend on the platform-wide Send text messages switch under All
            flags: while that one is off, nothing goes out by text whatever this says.
          </div>
        </>
      )}
    </Card>
  );
}

/** One delivery switch, drawn as a flag row so it reads as one more thing on this page. */
function DeliveryRow({
  settings,
  deliveryKey,
  busy,
  onToggle,
}: {
  settings: PlatformSettings;
  deliveryKey: DeliveryKey;
  busy: boolean;
  onToggle: (key: DeliveryKey, next: boolean) => void;
}): JSX.Element {
  const copy = DELIVERY_COPY[deliveryKey];
  const enabled = settings[deliveryKey];

  return (
    <div className="flag-row">
      <div className="flag-row__main">
        <div className="flag-row__name">
          {deliveryKey === 'deviceOtpEmailEnabled' ? <IconMail size={19} /> : <IconChat size={19} />}
          {copy.label}
        </div>
        <div className="flag-row__desc">{enabled ? copy.whenOn : copy.whenOff}</div>
      </div>
      <Badge tone={enabled ? 'green' : 'neutral'} dot>
        {enabled ? 'On' : 'Off'}
      </Badge>
      <Switch
        checked={enabled}
        disabled={busy}
        label={copy.label}
        onChange={(next) => onToggle(deliveryKey, next)}
      />
    </div>
  );
}

/** The two settings columns this section owns, named so a typo cannot reach a patch. */
type DeliveryKey = 'deviceOtpEmailEnabled' | 'deviceOtpSmsEnabled';

/**
 * Written out per switch rather than assembled from a template, because "the other way" is a
 * different sentence for each of the two and a generated one would read like a form letter on
 * the one control an operator reaches for in a hurry.
 */
const DELIVERY_COPY: Readonly<
  Record<
    DeliveryKey,
    {
      /** The way itself, for a confirmation that names which switch just moved. */
      channel: string;
      label: string;
      whenOn: string;
      whenOff: string;
      toast: { on: string; off: string };
    }
  >
> = {
  deviceOtpEmailEnabled: {
    channel: 'email',
    label: 'Send verification codes by email',
    whenOn: 'On. A customer signing in on a new phone is emailed a code, as long as their email address is confirmed.',
    whenOff:
      'Off. No code is emailed to anyone. A customer signing in on a new phone gets their code by text message instead, and if text message is off too they are only asked for their PIN.',
    toast: {
      on: 'A customer signing in on a new phone can now be emailed a code.',
      off: 'No code will be emailed. Customers get one by text message instead, or are asked for their PIN alone if that is off too.',
    },
  },
  deviceOtpSmsEnabled: {
    channel: 'text message',
    label: 'Send verification codes by text message',
    whenOn: 'On. A customer signing in on a new phone can be texted a code, as long as their phone number is confirmed.',
    whenOff:
      'Off. No code is texted to anyone. A customer signing in on a new phone gets their code by email instead, and if email is off too they are only asked for their PIN.',
    toast: {
      on: 'A customer signing in on a new phone can now be texted a code.',
      off: 'No code will be texted. Customers get one by email instead, or are asked for their PIN alone if that is off too.',
    },
  },
};

/**
 * One delivery column changed on a copy of the settings row.
 *
 * Spelled out per key rather than written as a computed property, so the result is still the
 * full settings shape the console holds rather than a widened object with a string index.
 */
function withDelivery(
  row: PlatformSettings,
  key: DeliveryKey,
  value: boolean,
): PlatformSettings {
  return key === 'deviceOtpEmailEnabled'
    ? { ...row, deviceOtpEmailEnabled: value }
    : { ...row, deviceOtpSmsEnabled: value };
}

function FlagRow({
  flag,
  featured = false,
  canWrite,
  busy,
  onToggle,
}: {
  flag: Flag;
  featured?: boolean;
  canWrite: boolean;
  busy: boolean;
  onToggle: (flag: Flag, next: boolean) => void;
}): JSX.Element {
  return (
    <div className={`flag-row${featured ? ' flag-row--feature' : ''}`}>
      <div className="flag-row__main">
        <div className="flag-row__name">
          {/* Two flags get the large treatment and they are not the same kind of thing, so the
              mark and the badge are keyed off the flag rather than off `featured`. A gift icon
              over the mandatory-KYC switch would be the wrong signal on the one control that
              stops customers transacting. */}
          {flag.key === FEATURED_KEY && <IconGift size={19} />}
          {flag.key === KYC_KEY && <IconAlert size={19} />}
          {humanize(flag.key)}
          <span className="flag-row__key">{flag.key}</span>
          {flag.key === FEATURED_KEY && <Badge tone="violet">Gift cards</Badge>}
          {flag.key === KYC_KEY && <Badge tone="amber">Affects existing customers</Badge>}
        </div>
        <div className="flag-row__desc">{flag.description}</div>
        <div className="flag-row__meta">
          {flag.enabled ? 'Enabled' : 'Disabled'} · updated {relativeTime(flag.updatedAt)}
          {flag.updatedByAdminId === null ? ' · never changed' : ''}
        </div>
      </div>
      <Badge tone={flag.enabled ? 'green' : 'neutral'} dot>
        {flag.enabled ? 'On' : 'Off'}
      </Badge>
      <Switch
        {...(featured ? { size: 'lg' as const } : {})}
        checked={flag.enabled}
        disabled={!canWrite || busy}
        label={`Toggle ${flag.key}`}
        onChange={(next) => onToggle(flag, next)}
      />
    </div>
  );
}
